/**
 * save-button.tsx — Phase 051 Plan 01 Task 2 (D-051-01 / D-051-05 / D-051-07).
 *
 * Mirrors `discard-all-button.tsx:22-38` byte-for-byte. Divergences vs
 * the discard sibling (intentional):
 *   - icon RotateCcw → Save
 *   - onClick clearAll → useSavePipeline().run
 *   - count baked into visible copy `Save N` / `Saving…`
 *   - `disabled={isInFlight}` attribute (D-051-05 visible single-flight)
 *   - aria-label switches `Save N unsaved edits` ↔ `Saving N unsaved edits`
 *   - className extends with `disabled:opacity-50 disabled:cursor-not-allowed`
 *   - data-testid `preview-save-button` (mirrors `preview-discard-all-button`)
 *
 * Inner gate (D-051-07): `dirtyCount === 0 → return null` mirrors the
 * DiscardAllButton render gate. Outer ABox gate enforced by the wrapping
 * `aboxElementMap != null` JSX conditional in preview-surface.tsx (D-048-03).
 *
 * AP8 — the button reads in-flight state from the hook (hook-local
 * `useState` synced from the module-level flag), NEVER from
 * `useMutationStore.status` (which is global across all 4 mutation tools).
 */
import { Save } from 'lucide-react'
import { useSavePipeline } from './use-save-pipeline'

export function SaveButton() {
  const { run, isInFlight, dirtyCount } = useSavePipeline()
  if (dirtyCount === 0) return null
  return (
    <button
      type="button"
      data-testid="preview-save-button"
      aria-label={
        isInFlight
          ? `Saving ${dirtyCount} unsaved edits`
          : `Save ${dirtyCount} unsaved edits`
      }
      disabled={isInFlight}
      onClick={() => { void run() }}
      className="px-2.5 py-1 text-[11px] font-medium rounded bg-transparent text-muted-foreground hover:text-foreground inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <Save size={12} strokeWidth={1.7} />
      {isInFlight ? 'Saving…' : `Save ${dirtyCount}`}
    </button>
  )
}
